/* eslint-disable unused-imports/no-unused-vars, @typescript-eslint/no-empty-function */

import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { FileUploadStatus } from '~components/inputs/FileUploadInput';
import { UploadStatus } from '~types/miscs/UploadStatus';
import type { ContractType, DappSettingsType, ProjectType } from '~types/ProjectType';
import type { CakeActivityType } from '~types/Streams';

interface ProjectConfigState {
  project: ProjectType | null;
  isLoading: boolean;
  isUpdating: boolean;
  error: string | null;
  imageUploader: Partial<Record<keyof FileUploadStatus, UploadStatus>>;
}

const initialState: ProjectConfigState = {
  project: null,
  isLoading: false,
  isUpdating: false,
  error: null,
  imageUploader: {},
};

const projectConfigState = createSlice({
  name: 'project-config',
  initialState,
  reducers: {
    getProjectConfig(state, action: PayloadAction<{ id: string; force: boolean }>) {
      state.isLoading = true;
      state.error = null;
    },
    setProjectConfig(state, action: PayloadAction<ProjectType>) {
      state.project = action.payload;
      state.isLoading = false;
    },
    cancelProjectConfigLoad(state) {
      state.isLoading = false;
    },
    setProjectConfigError(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.error = action.payload;
      state.project = null;
    },
    updateProjectContractConfig(state, action: PayloadAction<{ contract: ContractType; event: CakeActivityType }>) {},
    updateProjectDappConfig(state, action: PayloadAction<{ dapp: DappSettingsType; event?: CakeActivityType }>) {},
    updateProjectConfig(state, action: PayloadAction<ProjectType>) {
      state.isUpdating = true;
    },
    updateProjectConfigSuccess(state, action: PayloadAction<ProjectType>) {
      if (state.project) {
        state.project = action.payload;
      }
      state.isUpdating = false;
    },
    updateProjectFieldConfigSuccess(state, action: PayloadAction<{ field: string; value: any }>) {
      if (state.project) {
        const project = state.project;
        project[action.payload.field] = action.payload.value;
        state.project = project;
      }
      state.isUpdating = false;
    },
    updateProjectConfigError(state, action: PayloadAction<string>) {
      state.isUpdating = false;
      state.error = action.payload;
    },
    uploadImage(state, action: PayloadAction<{ file: File; key: string }>) {
      state.imageUploader = {
        ...state.imageUploader,
        [action.payload.key]: UploadStatus.LOADING,
      };
    },
    uploadImageError(state, action: PayloadAction<{ key: string; error: string }>) {
      state.imageUploader = {
        ...state.imageUploader,
        [action.payload.key]: UploadStatus.ERROR,
      };
    },
    uploadImageSuccess(state, action: PayloadAction<string>) {
      state.imageUploader = {
        ...state.imageUploader,
        [action.payload]: undefined,
      };
    },
    uploadGalleryImage(state, action: PayloadAction<{ file: File; key: string }>) {
      state.imageUploader = {
        ...state.imageUploader,
        [action.payload.key]: UploadStatus.LOADING,
      };
    },
    uploadGalleryImageError(state, action: PayloadAction<{ error: File; key: string }>) {
      const { key } = action.payload;
      state.imageUploader = {
        ...state.imageUploader,
        [key]: UploadStatus.ERROR,
      };
    },
    uploadGalleryImageSuccess(state, action: PayloadAction<{ path: string; key: string }>) {
      const { key } = action.payload;
      state.imageUploader = {
        ...state.imageUploader,
        [key]: undefined,
      };
    },
    deleteGalleryImage(state, action: PayloadAction<number>) {},
    deleteGalleryImageSuccess(state) {},
    deleteGalleryImageError(state) {},
  },
});

export const {
  getProjectConfig,
  setProjectConfig,
  cancelProjectConfigLoad,
  setProjectConfigError,
  updateProjectConfig,
  updateProjectConfigSuccess,
  updateProjectConfigError,
  updateProjectContractConfig,
  updateProjectDappConfig,
  uploadImage,
  uploadImageError,
  uploadImageSuccess,
  updateProjectFieldConfigSuccess,
  uploadGalleryImage,
  uploadGalleryImageError,
  uploadGalleryImageSuccess,
  deleteGalleryImage,
  deleteGalleryImageSuccess,
  deleteGalleryImageError,
} = projectConfigState.actions;
export default projectConfigState.reducer;
